<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-12">
        <h1 class="h2 mb-1 fw-bold">Acreage Reports</h1>
        <p class="text-muted">View acreage sprayed by organization with detailed drill-downs</p>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters Card -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Date Range -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Start Date</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterStartDate"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label small text-body-secondary">End Date</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterEndDate"
            />
          </div>

          <!-- Organization Filter -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Organization</label>
            <select
              class="form-select"
              [(ngModel)]="filterOrganization"
            >
              <option value="">All Organizations</option>
              <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
            </select>
          </div>

          <!-- Generate Report Button -->
          <div class="col-md-2">
            <button
              type="button"
              class="btn btn-primary w-100"
              (click)="generateReport()"
              [disabled]="loading || !filterStartDate || !filterEndDate"
            >
              <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!loading" class="bi bi-search me-2"></i>
              Generate
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-2">Loading report data...</p>
    </div>

    <!-- Organization Detail View -->
    <div *ngIf="!loading && orgDetailData">
      <!-- Export Button -->
      <div class="d-flex justify-content-end mb-3">
        <button
          type="button"
          class="btn btn-primary"
          (click)="exportToPdf()"
          [disabled]="exportLoading"
        >
          <span *ngIf="exportLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportLoading" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>

      <!-- Summary Card -->
      <div class="card mb-4">
        <div class="card-body text-center py-5">
          <h3 class="text-muted mb-2">{{ orgDetailData.organizationName }}</h3>
          <div class="display-4 fw-bold text-primary mb-2">
            {{ formatNumber(orgDetailData.totalAcreage) }}
          </div>
          <p class="text-muted mb-1">Total Acres Sprayed</p>
          <p class="text-muted small">
            {{ formatDate(orgDetailData.startDate) }} - {{ formatDate(orgDetailData.endDate) }}
          </p>
          <p class="text-muted small">
            <i class="bi bi-airplane me-1"></i>
            {{ orgDetailData.totalFlights }} flights
          </p>
        </div>
      </div>

      <!-- Drill-Down Sections -->
      <div class="row g-4">
        <!-- By Drone -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-robot me-2"></i>
                By Drone
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Drone Name</th>
                    <th class="text-end">Total Acreage</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byDrone.length === 0">
                    <td colspan="3" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <tr *ngFor="let item of orgDetailData.byDrone">
                    <td>{{ item.name }}</td>
                    <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                    <td class="text-end">{{ item.flightCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- By Pilot -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-person-badge me-2"></i>
                By Pilot
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Pilot Name</th>
                    <th class="text-end">Total Acreage</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byPilot.length === 0">
                    <td colspan="3" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <tr *ngFor="let item of orgDetailData.byPilot">
                    <td>{{ item.name }}</td>
                    <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                    <td class="text-end">{{ item.flightCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- By Field -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-map me-2"></i>
                By Field
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Field Name</th>
                    <th class="text-end">Total Acreage</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byField.length === 0">
                    <td colspan="3" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <tr *ngFor="let item of orgDetailData.byField">
                    <td>{{ item.name }}</td>
                    <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                    <td class="text-end">{{ item.flightCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- By Week -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-calendar-week me-2"></i>
                By Week
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th class="text-end">Total Acreage</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byWeek.length === 0">
                    <td colspan="3" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <tr *ngFor="let item of orgDetailData.byWeek">
                    <td>
                      <small class="text-body-secondary">{{ formatDate(item.weekStartDate) }} - {{ formatDate(item.weekEndDate) }}</small>
                    </td>
                    <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                    <td class="text-end">{{ item.flightCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- All Organizations Summary View -->
    <div *ngIf="!loading && summaryData">
      <!-- Export Button -->
      <div class="d-flex justify-content-end mb-3">
        <button
          type="button"
          class="btn btn-primary"
          (click)="exportToPdf()"
          [disabled]="exportLoading"
        >
          <span *ngIf="exportLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportLoading" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>

      <div class="card overflow-hidden">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-building me-2"></i>
            All Organizations - {{ formatDate(summaryData.startDate) }} to {{ formatDate(summaryData.endDate) }}
          </h6>
        </div>
        <div class="table-responsive">
          <table class="table data-table table-hover mb-0">
            <thead>
              <tr>
                <th>Organization Name</th>
                <th class="text-end">Total Acreage</th>
                <th class="text-end">Flight Count</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="summaryData.organizations.length === 0">
                <td colspan="3" class="text-center text-muted py-4">
                  No data for selected period
                </td>
              </tr>
              <tr *ngFor="let org of summaryData.organizations">
                <td>{{ org.organizationName }}</td>
                <td class="text-end">{{ formatNumber(org.totalAcreage) }}</td>
                <td class="text-end">{{ org.flightCount }}</td>
              </tr>
            </tbody>
            <tfoot class="bg-body-tertiary" *ngIf="summaryData.organizations.length > 0">
              <tr class="fw-bold">
                <td>Grand Total</td>
                <td class="text-end">{{ formatNumber(summaryData.grandTotalAcreage) }}</td>
                <td class="text-end">{{ summaryData.grandTotalFlights }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div *ngIf="!loading && !errorMessage && !orgDetailData && !summaryData" class="text-center py-5">
      <i class="bi bi-bar-chart display-1 text-muted"></i>
      <p class="text-muted mt-3">Select your filters and click Generate to view the report.</p>
    </div>
  </div>
</div>
